import * as THREE from 'three';
import { makeRng } from '../core/Rand.js';
import { sharedUniforms, registerMaterial } from './lightshaders/inject.js';
import { UNIT_SCALE } from './Sky.js';

/**
 * Env — image based lighting. Owner: lighting.
 *
 * Three layers, in increasing locality:
 *
 *  1. `scene.environment`  PMREM of the procedural sky cube. Real radiance, so
 *     ambient specular matches the analytic sky with no fudge factor.
 *  2. Local reflection probes  CubeCamera-baked at the interior positions the
 *     level publishes in `lightHints`, PMREM'd, and bound as `material.envMap`
 *     on the meshes inside each probe's influence box. Interiors therefore
 *     reflect their own walls and practicals, not the sky.
 *  3. Irradiance volume  a 3D grid over `navBounds` holding one-bounce
 *     irradiance split into an upper and lower hemisphere plus a sky-visibility
 *     term. The visibility multiplies the IBL (so an interior is not lit by the
 *     outdoor sky), the irradiance adds the bounce that grounds props.
 *
 * Driven by Lighting.js — it is not a separate Engine system because main.js
 * (spine, not ours) decides which systems exist.
 */

export function createEnv() {
  const rng = makeRng(0x1bad1dea);

  let ctx = null;
  /**
   * TWO generators, and a persistent output target for every bake. Both halves
   * of that matter — see `buildSkyEnvironment` and `bakeProbes`.
   *
   * `PMREMGenerator` keeps one ping-pong target sized for the last cubemap it
   * saw, and re-allocates it whenever the size changes. The sky cube is 1024/face
   * and the reflection probes are 256, so a single generator threw its ping-pong
   * away and rebuilt it twice per environment refresh. One generator per size
   * means `_allocateTargets` runs exactly once each, at boot.
   */
  let pmremSky = null;
  let pmremProbe = null;
  let skyEnv = null;
  let skyEnvRT = null;            // persistent PMREM output for the sky

  const probes = [];              // { position, box, size, cubeRT, cubeCam, envRT, envMap, intensity }
  const probeMaterialCache = new Map(); // originalMaterial -> Map(probeIndex, clone)
  const probeMaterialOwners = new Set(); // meshes currently wearing a probe clone

  const iv = {
    enabled: false,
    dims: new THREE.Vector3(1, 1, 1),
    min: new THREE.Vector3(),
    size: new THREE.Vector3(1, 1, 1),
    skyTex: null,
    groundTex: null,
    skyData: null,
    groundData: null,
    cells: 0,
    baked: 0,
    ms: 0,
  };

  /* ---------------- sky PMREM ---------------- */

  /**
   * PMREM the sky cube into a target we keep forever.
   *
   * ### Why this used to leak 43 textures per `setTimeOfDay`
   *
   * `PMREMGenerator.fromCubemap(cube)` allocates a fresh `WebGLRenderTarget`
   * every call and returns its texture. The old code disposed the *previous
   * texture* — `prev?.dispose()` — and that frees nothing at all:
   * `WebGLTextures.deallocateTexture` bails on `__webglInit === undefined`, and
   * `setupRenderTarget` never sets `__webglInit`. So a render-target texture is
   * the one kind of texture whose `.dispose()` is a guaranteed no-op. The only
   * thing that frees it is `renderTarget.dispose()`.
   *
   * Rather than dispose-and-reallocate, `fromCubemap` takes an existing target
   * as its second argument and renders into it, so after boot this path
   * allocates nothing at all. Same for every reflection probe below.
   */
  function buildSkyEnvironment() {
    const cube = ctx.sky?.cubeTexture;
    if (!cube) return;
    if (!pmremSky) {
      pmremSky = new THREE.PMREMGenerator(ctx.renderer);
      pmremSky.compileCubemapShader();
    }
    skyEnvRT = pmremSky.fromCubemap(cube, skyEnvRT);
    skyEnv = skyEnvRT.texture;
    skyEnv.name = 'skyEnvPMREM';
    ctx.scene.environment = skyEnv;
    ctx.scene.environmentIntensity = 1.0;

    /* The viewmodel takes the same IBL, at the same scale.
     *
     * Two things are fixed by claiming `viewScene.environment` here rather than
     * leaving it unset:
     *
     *  1. It is the only environment in the game whose radiance is in scene
     *     units (CONTRACTS.md :: Scene units). The fallback the weapons team
     *     builds if this slot is empty is a hand-valued LDR studio rig, which
     *     is a third absolute scale for the frame to reconcile — half of why
     *     the gun clipped white while the street was black.
     *  2. That fallback is `PMREMGenerator.fromScene(rig, 0.06)`, and 0.06 rad
     *     is past what PMREM can filter at its top mip: it warned twice on every
     *     boot and silently clipped the blur, so IBL roughness response was
     *     wrong for the one object that is 30 cm from the camera all game.
     *
     * Lighting runs at priority 35 and weapons at 60, so this always lands
     * first. `installEnvironment` sees a populated slot and adopts it.
     * The strength is re-asserted per frame by the render team's viewmodel
     * light sync (src/render/ProbeRig.js :: syncViewmodelLights).
     */
    if (ctx.viewScene) {
      ctx.viewScene.environment = skyEnv;
      if (ctx.viewScene.environmentIntensity === undefined || ctx.viewScene.environmentIntensity === 1) {
        ctx.viewScene.environmentIntensity = 0.85;
      }
    }
  }

  /* ---------------- local probes ---------------- */

  function collectProbeSites() {
    const hints = ctx.level?.lightHints || [];
    const sites = [];
    for (const h of hints) {
      if (!h || !h.position) continue;
      const type = h.type || '';
      if (type === 'probe' || type === 'reflection' || type === 'interior' || type === 'room') {
        sites.push({
          position: new THREE.Vector3().copy(h.position),
          radius: h.radius || 8,
          intensity: h.intensity ?? 1,
        });
      }
    }
    if (sites.length === 0) {
      // Fall back to interior rooms the level flags on its root.
      const rooms = ctx.level?.interiors || ctx.level?.rooms;
      if (Array.isArray(rooms)) {
        for (const r of rooms) {
          const c = r.center || r.position;
          if (!c) continue;
          sites.push({ position: new THREE.Vector3().copy(c), radius: r.radius || 8, intensity: 1 });
        }
      }
    }
    return sites.slice(0, 6);
  }

  /**
   * Re-bake the local reflection probes IN PLACE.
   *
   * The old version disposed every probe and built new ones on each refresh,
   * which cost two render targets per probe per `setTimeOfDay` and freed one of
   * them (`envMap.dispose()` on a render-target texture is a no-op — see
   * `buildSkyEnvironment`). Six probes, five times of day: 43 textures a shot,
   * 209 across the `light_tod_*` set, and every draw-call and memory number
   * captured afterwards was standing on it.
   *
   * Re-baking in place also fixes a silent correctness bug: `probeMaterialCache`
   * is keyed by the *original* material and was never invalidated, so after the
   * first bake the cached clones still pointed at the first bake's cubes. Every
   * probe-lit surface in the game kept the environment it was given at boot, no
   * matter what the sun did. Now the texture objects are stable and their
   * contents are re-rendered, so the cache is correct by construction.
   */
  function bakeProbes(hideForBake) {
    const sites = collectProbeSites();
    if (!sites.length) { disposeProbes(); return; }

    // 256 keeps PMREM's blur inside its sample budget (128 warns at mip 0).
    const size = ctx.quality === 'low' ? 128 : 256;
    if (!pmremProbe) {
      pmremProbe = new THREE.PMREMGenerator(ctx.renderer);
      pmremProbe.compileCubemapShader();
    }
    const restore = hideForBake ? hideForBake() : null;

    // Surplus slots (a level published fewer probe hints than last time).
    let rebound = false;
    while (probes.length > sites.length) { releaseProbe(probes.pop()); rebound = true; }

    for (let i = 0; i < sites.length; i++) {
      const s = sites[i];
      let p = probes[i];
      if (!p || p.size !== size) {
        if (p) releaseProbe(p);
        const rt = new THREE.WebGLCubeRenderTarget(size, {
          type: THREE.HalfFloatType,
          format: THREE.RGBAFormat,
          generateMipmaps: false,
          minFilter: THREE.LinearFilter,
          magFilter: THREE.LinearFilter,
          colorSpace: THREE.LinearSRGBColorSpace,
        });
        p = {
          size,
          cubeRT: rt,
          cubeCam: new THREE.CubeCamera(0.15, 200, rt),
          envRT: null,
          envMap: null,
          position: new THREE.Vector3(),
          box: new THREE.Box3(),
          radius: 8,
          intensity: 1,
        };
        probes[i] = p;
        rebound = true;   // a new texture object — cached clones must be rebuilt
      }
      p.position.copy(s.position);
      p.radius = s.radius;
      p.intensity = s.intensity;
      p.box.setFromCenterAndSize(s.position, new THREE.Vector3(s.radius * 2, s.radius * 2, s.radius * 2));
      p.cubeCam.position.copy(s.position);
      p.cubeCam.update(ctx.renderer, ctx.scene);
      p.envRT = pmremProbe.fromCubemap(p.cubeRT.texture, p.envRT);
      p.envMap = p.envRT.texture;
      p.envMap.name = 'probeEnv';
    }

    if (rebound) clearProbeMaterials();
    restore?.();
    assignProbes();
  }

  const _center = new THREE.Vector3();
  const stats = { probeMeshes: 0, rejectedTooBig: 0 };

  /**
   * Bind each mesh that genuinely sits inside a probe's influence to that
   * probe's cubemap.
   *
   * The size test is the whole point and it used to be missing. Selection was
   * "bounding-sphere centre is within probe.radius", with no bound on how big
   * the mesh is — and this level merges its ground, walls and props into a
   * handful of batched meshes per material (see kit/Street.js :: groundPatch).
   * The ground batch spans the entire 76 m map, so its centre lands near the
   * middle of the level, inside some interior probe, and the *whole map's
   * ground* then reflected one 256px cube baked inside one room. That is why
   * every floor in the build read as a flat dark plane while the walls beside
   * it read correctly: the walls are separate batches that fell outside, the
   * floor was one mesh that got captured.
   *
   * Requiring the mesh to fit inside the probe sphere keeps local reflections
   * where they belong — props and small interior surfaces — and leaves large
   * merged geometry on the sky environment, which is occluded per-pixel by
   * aaaSkyVisibility instead.
   */
  function assignProbes() {
    if (!probes.length || !ctx.level?.root) return;
    stats.probeMeshes = 0;
    stats.rejectedTooBig = 0;
    const _s = new THREE.Vector3();
    ctx.level.root.traverse((obj) => {
      if (!obj.isMesh || !obj.material || Array.isArray(obj.material)) return;
      if (obj.userData.aaaHelper) return;
      /**
       * FINDINGS F11. For an InstancedMesh, `geometry.boundingSphere` is ONE
       * instance — a 35 cm rubble chunk, a 17 cm grass tuft — not the mesh's
       * world extent. Sizing the probe test from it meant every instanced prop
       * in the map "fitted" every probe: 36 of 64 level meshes ended up on a
       * single indoor probe's 256 px cube, so every crate, sandbag, tyre and
       * grass tuft in the level was reflecting one dark room while the merged
       * statics 30 cm away reflected the sky. three maintains a mesh-level
       * sphere on InstancedMesh itself; that is the one that means anything
       * here.
       */
      let bs;
      if (obj.isInstancedMesh) {
        if (!obj.boundingSphere) obj.computeBoundingSphere();
        bs = obj.boundingSphere;
      } else {
        if (!obj.geometry.boundingSphere) obj.geometry.computeBoundingSphere();
        bs = obj.geometry.boundingSphere;
      }
      if (!bs) return;
      _center.copy(bs.center).applyMatrix4(obj.matrixWorld);
      // world-space radius, honouring a scaled instance
      obj.matrixWorld.decompose(_s, _q, _s2);
      const scale = Math.max(Math.abs(_s2.x), Math.abs(_s2.y), Math.abs(_s2.z)) || 1;
      const rWorld = bs.radius * scale;

      let best = -1;
      let bestD = Infinity;
      for (let i = 0; i < probes.length; i++) {
        const p = probes[i];
        const d = _center.distanceTo(p.position);
        // The mesh must actually FIT inside the probe's influence, not merely
        // have its centre in it.
        if (rWorld > p.radius * 0.75 || d + rWorld > p.radius * 1.15) continue;
        if (d < bestD) { bestD = d; best = i; }
      }
      if (best < 0) {
        if (obj.userData.aaaOriginalMaterial) obj.material = obj.userData.aaaOriginalMaterial;
        probeMaterialOwners.delete(obj);
        stats.rejectedTooBig++;
        return;
      }

      const original = obj.userData.aaaOriginalMaterial || obj.material;
      obj.userData.aaaOriginalMaterial = original;
      let byProbe = probeMaterialCache.get(original);
      if (!byProbe) { byProbe = new Map(); probeMaterialCache.set(original, byProbe); }
      let clone = byProbe.get(best);
      if (!clone) {
        clone = original.clone();
        clone.name = `${original.name || 'mat'}__probe${best}`;
        clone.envMap = probes[best].envMap;
        clone.envMapIntensity = (original.envMapIntensity ?? 1) * probes[best].intensity;
        registerMaterial(clone);
        // This material's reflection is already the local room. Do not let the
        // sky-visibility term delete it as well — that double penalty is what
        // crushed every probe-lit interior surface to black.
        clone.defines = clone.defines || {};
        clone.defines.AAA_LOCAL_ENV = 1;
        clone.needsUpdate = true;
        byProbe.set(best, clone);
      }
      obj.material = clone;
      probeMaterialOwners.add(obj);
      stats.probeMeshes++;
    });
  }
  const _q = new THREE.Quaternion();
  const _s2 = new THREE.Vector3();

  /**
   * Free one probe slot. Note `envRT.dispose()`, not `envMap.dispose()` — the
   * texture-level call frees nothing for a render-target texture.
   */
  function releaseProbe(p) {
    if (!p) return;
    p.cubeRT?.dispose();
    p.envRT?.dispose();
    p.envRT = null;
    p.envMap = null;
  }

  /** Drop every probe-local material clone and put the originals back. */
  function clearProbeMaterials() {
    for (const obj of probeMaterialOwners) {
      if (obj.userData.aaaOriginalMaterial) obj.material = obj.userData.aaaOriginalMaterial;
    }
    probeMaterialOwners.clear();
    for (const byProbe of probeMaterialCache.values()) {
      for (const clone of byProbe.values()) clone.dispose();
    }
    probeMaterialCache.clear();
  }

  function disposeProbes() {
    clearProbeMaterials();
    for (const p of probes) releaseProbe(p);
    probes.length = 0;
  }

  /* ---------------- irradiance volume ---------------- */

  // Deterministic Fibonacci sphere — even coverage, no clumping, no RNG drift.
  function sphereDirs(n) {
    const out = [];
    const ga = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < n; i++) {
      const y = 1 - (2 * (i + 0.5)) / n;
      const r = Math.sqrt(Math.max(0, 1 - y * y));
      const th = ga * i + rng() * 0.0001;
      out.push(new THREE.Vector3(Math.cos(th) * r, y, Math.sin(th) * r));
    }
    return out;
  }

  const _ray = new THREE.Raycaster();
  const _origin = new THREE.Vector3();
  const _tmpN = new THREE.Vector3();
  const _shadowOrigin = new THREE.Vector3();
  let lastBakeSun = new THREE.Vector3(0, -1, 0);

  function makeCaster() {
    // Prefer the physics BVH when the real physics system is up — a naive
    // Raycaster over a shipping level is orders of magnitude slower.
    const phys = ctx.physics;
    if (phys && typeof phys.raycast === 'function' && phys.MASK) {
      const mask = phys.MASK.WORLD | phys.MASK.PROP;
      return (origin, dir, far) => phys.raycast(origin, dir, far, mask) || null;
    }

    const targets = [];
    const level = ctx.level;
    if (level?.collision?.length) targets.push(...level.collision);
    else if (level?.root) targets.push(level.root);

    _ray.firstHitOnly = true;
    return (origin, dir, far) => {
      _ray.set(origin, dir);
      _ray.near = 0.02;
      _ray.far = far;
      const hits = _ray.intersectObjects(targets, true);
      for (const h of hits) {
        const o = h.object;
        if (o.userData.aaaHelper || o.userData.noCollide) continue;
        // collision proxies are intentionally visible:false — do not skip them
        if (!o.visible && !o.userData.collisionProxy) continue;
        return h;
      }
      return null;
    };
  }

  const SURFACE_ALBEDO = {
    concrete: [0.34, 0.33, 0.31], asphalt: [0.09, 0.09, 0.09],
    dirt: [0.24, 0.19, 0.13], sand: [0.42, 0.36, 0.26],
    metal: [0.30, 0.30, 0.31], wood: [0.28, 0.20, 0.12],
    glass: [0.10, 0.11, 0.12], fabric: [0.30, 0.27, 0.22],
    foliage: [0.14, 0.24, 0.10], water: [0.06, 0.08, 0.10],
    flesh: [0.42, 0.28, 0.24],
  };
  const _alb = new THREE.Color();

  function albedoOf(object) {
    const m = object?.material;
    const mat = Array.isArray(m) ? m[0] : m;
    if (mat && mat.color && mat.visible !== false) return mat.color;
    const surf = object?.userData?.surface;
    const a = SURFACE_ALBEDO[surf];
    if (a) return _alb.setRGB(a[0], a[1], a[2]);
    return null;
  }

  function bakeIrradianceVolume(opts = {}) {
    const sky = ctx.sky;
    if (!sky) return;

    const bounds = new THREE.Box3();
    if (ctx.level?.navBounds) bounds.copy(ctx.level.navBounds);
    else if (ctx.level?.bounds) bounds.copy(ctx.level.bounds);
    else if (ctx.level?.root) bounds.setFromObject(ctx.level.root);
    else return;
    if (bounds.isEmpty()) return;

    bounds.expandByScalar(2.0);
    bounds.min.y = Math.max(bounds.min.y, -1.5);
    bounds.max.y = Math.min(bounds.max.y, bounds.min.y + 34);

    const size = new THREE.Vector3();
    bounds.getSize(size);

    let spacingXZ = opts.spacing ?? 2.4;
    /**
     * Vertical resolution is the binding constraint, not horizontal. Capped at
     * 10 layers this volume ran 1.94 m per layer over a 15.5 m box, so a point
     * 1 m under a roof blended halfway into the cell *above* the roof — which
     * is open sky. That is what put cloud reflections on interior ceilings, and
     * it is also what defeated the volumetric shafts: the dust term reads this
     * volume's visibility channel to decide it is indoors, and at head height
     * on a mezzanine the interpolated answer came back "outdoors".
     * 16 layers is ~1.0 m, which puts the whole of a normal storey between two
     * layers that are both genuinely inside it.
     */
    const spacingY = opts.spacingY ?? 1.1;
    let nx = THREE.MathUtils.clamp(Math.round(size.x / spacingXZ) + 1, 4, 28);
    const ny = THREE.MathUtils.clamp(Math.round(size.y / spacingY) + 1, 3, 16);
    let nz = THREE.MathUtils.clamp(Math.round(size.z / spacingXZ) + 1, 4, 28);
    // Keep the bake bounded no matter how big the level gets.
    const maxCells = opts.maxCells ?? 16000;
    while (nx * ny * nz > maxCells && (nx > 5 || nz > 5)) {
      nx = Math.max(5, Math.floor(nx * 0.85));
      nz = Math.max(5, Math.floor(nz * 0.85));
    }

    const rays = opts.rays ?? (ctx.quality === 'low' ? 16 : 32);
    const dirs = sphereDirs(rays);
    // Sky radiance for each of the fixed probe directions — evaluated once per
    // bake, not once per ray, so a ray that escapes through a doorway can carry
    // real daylight into the room for free.
    const skyGrade = ctx.lighting?.skyTint;
    const skyRad = dirs.map((d) => {
      const v = ctx.sky.sampleRadiance ? ctx.sky.sampleRadiance(d, new THREE.Vector3()) : new THREE.Vector3();
      // Same unit-luminance grade the sky fill carries, so a ray that escapes
      // through a doorway brings in the graded sky rather than the raw one.
      if (skyGrade) v.set(v.x * skyGrade.r, v.y * skyGrade.g, v.z * skyGrade.b);
      return v;
    });
    const cast = makeCaster();
    const budgetMs = opts.budgetMs ?? 2200;
    const t0 = performance.now();

    const cells = nx * ny * nz;
    const skyData = new Uint16Array(cells * 4);
    const gndData = new Uint16Array(cells * 4);
    const half = THREE.DataUtils.toHalfFloat;

    const sunIll = sky.derived.sunIlluminance;
    const skyIll = sky.derived.skyIlluminance;
    const sunDir = sky.sunDirection;

    /**
     * Chromaticity grade on the one-bounce term.
     *
     * In this map the dominant ambient in a shaded pocket is light that has
     * come off a sunlit sand facade at albedo (0.42, 0.36, 0.26), so the
     * shadow ends up warmer than it should — measured, shadowed ground at
     * linear R/B 2.10 against a sunlit 2.52, which is the review's "the shadow
     * is warmer than the key". `ambientCool` pulls that bounce toward the sky's
     * own chromaticity at CONSTANT LUMINANCE: it stands for the sky the
     * bouncing surface itself sees and for the second bounce this single-bounce
     * model never traces, and because it is unit luminance it cannot move the
     * exposure anchor or the acceptance measurement.
     */
    const cool = THREE.MathUtils.clamp(ctx.lighting?.grading?.ambientCool ?? 0, 0, 1);
    const tint = ctx.lighting?.skyTint;
    const skyChroma = (() => {
      const c = sky.skyColor ? sky.skyColor.clone() : new THREE.Color(1, 1, 1);
      if (tint) c.multiply(tint);
      const l = Math.max(1e-4, 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b);
      return c.multiplyScalar(1 / l);
    })();
    /** Lerp `(r,g,b)` toward the graded sky chromaticity, preserving luminance. */
    const _bt = new THREE.Vector3();
    const coolBounce = (r, g, b) => {
      if (cool <= 0) return _bt.set(r, g, b);
      const l = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      if (l <= 1e-9) return _bt.set(r, g, b);
      return _bt.set(
        THREE.MathUtils.lerp(r, skyChroma.r * l, cool),
        THREE.MathUtils.lerp(g, skyChroma.g * l, cool),
        THREE.MathUtils.lerp(b, skyChroma.b * l, cool),
      );
    };
    // 26 m stopped short of the far side of the market square, so the sunlit
    // facade that should be bouncing into the shadowed lane simply was not in
    // any probe's reach.
    const maxDist = opts.maxDist ?? 42;

    // Analytic default used when we run out of budget.
    const gb = sky.derived.groundBounce;
    const fbUp = new THREE.Vector3(skyIll.x, skyIll.y, skyIll.z).multiplyScalar(0.55 * UNIT_SCALE);
    const fbDn = new THREE.Vector3(gb.x, gb.y, gb.z).multiplyScalar(0.30 * UNIT_SCALE);

    /**
     * Probes that land inside solid geometry produce garbage — every ray hits
     * at 2 cm, so visibility reads 0 and irradiance reads black, and trilinear
     * filtering then smears that black into every surface within a cell of it.
     * On a 2.4 m grid over a dense urban block a large fraction of the volume is
     * inside a wall. Nudge such a probe to the nearest position that can see
     * something and bake there instead.
     */
    const RELOC = [
      [0, 0, 0],
      [0, 1, 0], [0, -1, 0],
      [1, 0, 0], [-1, 0, 0], [0, 0, 1], [0, 0, -1],
      [0, 1.9, 0], [0.9, 0.6, 0.9], [-0.9, 0.6, -0.9],
    ];
    const _probeDirs = [
      new THREE.Vector3(1, 0, 0), new THREE.Vector3(-1, 0, 0),
      new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, -1, 0),
      new THREE.Vector3(0, 0, 1), new THREE.Vector3(0, 0, -1),
    ];
    const _reloc = new THREE.Vector3();
    /** Open space around p: 0 = fully embedded in geometry, 1 = clear. */
    function openness(p) {
      let open = 0;
      for (const d of _probeDirs) {
        const h = cast(p, d, 0.85);
        if (!h || h.distance > 0.8) open++;
      }
      return open / 6;
    }

    let baked = 0;
    let relocated = 0;
    let stranded = 0;
    for (let iz = 0; iz < nz; iz++) {
      for (let iy = 0; iy < ny; iy++) {
        for (let ix = 0; ix < nx; ix++) {
          const idx = (iz * ny * nx + iy * nx + ix);
          const o = idx * 4;

          const u = nx > 1 ? ix / (nx - 1) : 0.5;
          const v = ny > 1 ? iy / (ny - 1) : 0.5;
          const w = nz > 1 ? iz / (nz - 1) : 0.5;
          _origin.set(
            bounds.min.x + size.x * u,
            bounds.min.y + size.y * v,
            bounds.min.z + size.z * w,
          );

          let upR = 0, upG = 0, upB = 0, upN = 0;
          let dnR = 0, dnG = 0, dnB = 0, dnN = 0;
          // Sky visibility is cosine-weighted over the UPPER hemisphere only:
          // a point on open ground must come out at 1.0, not 0.5, because the
          // IBL irradiance it multiplies is already hemispherical.
          let visOpen = 0, visTotal = 0;
          let occluded = 0, rays = 0;

          const overBudget = performance.now() - t0 > budgetMs;
          if (!overBudget) {
            // --- relocate out of solid
            if (openness(_origin) < 0.34) {
              let bestOpen = -1;
              for (let ri = 1; ri < RELOC.length; ri++) {
                _reloc.set(
                  _origin.x + RELOC[ri][0] * spacingXZ * 0.5,
                  _origin.y + RELOC[ri][1] * spacingY * 0.5,
                  _origin.z + RELOC[ri][2] * spacingXZ * 0.5,
                );
                const op = openness(_reloc);
                if (op > bestOpen) { bestOpen = op; _shadowOrigin.copy(_reloc); }
                if (op > 0.66) break;
              }
              if (bestOpen > 0.34) { _origin.copy(_shadowOrigin); relocated++; }
              else stranded++;
            }

            for (let di = 0; di < dirs.length; di++) {
              const d = dirs[di];
              // Cosine weight: E = 2pi * mean( L cos(theta) ) for a uniform
              // hemisphere sample set. The old estimator dropped the cosine, so
              // a grazing ray into a shadowed wall counted as much as a ray
              // straight up at the open sky.
              const cw = Math.abs(d.y);
              const vw = Math.max(0, d.y);
              visTotal += vw;
              rays++;
              const hit = cast(_origin, d, maxDist);
              if (!hit) {
                visOpen += vw;
                // The sky itself is part of the local irradiance. This is the
                // difference between a room that reads as an interior and a
                // room that reads as a black hole.
                const sr = skyRad[di];
                if (d.y >= 0) { upR += sr.x * cw; upG += sr.y * cw; upB += sr.z * cw; upN++; }
                else { dnR += sr.x * cw; dnG += sr.y * cw; dnB += sr.z * cw; dnN++; }
                continue;
              }
              occluded++;

              const alb = albedoOf(hit.object);
              const ar = alb ? alb.r : 0.32;
              const ag = alb ? alb.g : 0.32;
              const ab = alb ? alb.b : 0.32;

              if (hit.face && hit.object) _tmpN.copy(hit.face.normal).transformDirection(hit.object.matrixWorld);
              else if (hit.normal) _tmpN.copy(hit.normal);
              else _tmpN.copy(d).negate();
              _tmpN.normalize();
              if (_tmpN.dot(d) > 0) _tmpN.negate();     // always face the probe
              const nl = Math.max(0, _tmpN.dot(sunDir));

              // One shadow ray so interiors do not receive a fake sun bounce.
              let shadow = 0;
              if (nl > 0.02) {
                _shadowOrigin.copy(hit.point).addScaledVector(_tmpN, 0.05);
                shadow = cast(_shadowOrigin, sunDir, 90) ? 0 : 1;
              }
              // Cheap proxy for how much sky the bouncing surface itself sees.
              const skyFrac = 0.3 + 0.5 * Math.max(0, _tmpN.y);

              const k = UNIT_SCALE / Math.PI;
              const b3 = coolBounce(
                (sunIll.x * nl * shadow + skyIll.x * skyFrac) * ar * k,
                (sunIll.y * nl * shadow + skyIll.y * skyFrac) * ag * k,
                (sunIll.z * nl * shadow + skyIll.z * skyFrac) * ab * k,
              );
              const er = b3.x, eg = b3.y, eb = b3.z;

              if (d.y >= 0) { upR += er * cw; upG += eg * cw; upB += eb * cw; upN++; }
              else { dnR += er * cw; dnG += eg * cw; dnB += eb * cw; dnN++; }
            }
            baked++;
          } else {
            visOpen = 1; visTotal = 1;
            // A budget overrun used to write ZERO upper-hemisphere irradiance,
            // which is a far worse answer than a coarse analytic one: every
            // up-facing surface in the unbaked region lost its whole ambient.
            upR = fbUp.x; upG = fbUp.y; upB = fbUp.z; upN = 2 * Math.PI;
            dnR = fbDn.x; dnG = fbDn.y; dnB = fbDn.z; dnN = 2 * Math.PI;
          }

          /**
           * One bounce is not GI. In an enclosed space each bounce returns
           * roughly rho of what arrived, so the series sums to 1/(1 - rho*f)
           * where f is the fraction of the hemisphere that is other surfaces
           * rather than sky. rho ~ 0.35 for this palette of concrete, plaster
           * and sand; f is measured per probe as the occluded ray fraction.
           * A room comes out ~1.5x its single-bounce value, open ground ~1.0x,
           * which is exactly where the missing light in the alley and the
           * interior was.
           */
          const f = rays ? occluded / rays : 0;
          const interreflect = 1 / (1 - 0.35 * f);

          // E = integral( L cos ) ~= (2pi / N_hemisphere) * sum( L_i cos_i ).
          const kUp = upN ? 2 * Math.PI / upN : 0;
          const kDn = dnN ? 2 * Math.PI / dnN : 0;
          const vis = visTotal > 1e-4 ? visOpen / visTotal : 1;
          const clampHalf = (v) => half(Math.min(60000, Math.max(0, v)));

          skyData[o + 0] = clampHalf(upR * kUp * interreflect);
          skyData[o + 1] = clampHalf(upG * kUp * interreflect);
          skyData[o + 2] = clampHalf(upB * kUp * interreflect);
          skyData[o + 3] = half(THREE.MathUtils.clamp(vis, 0, 1));

          gndData[o + 0] = clampHalf(dnR * kDn * interreflect);
          gndData[o + 1] = clampHalf(dnG * kDn * interreflect);
          gndData[o + 2] = clampHalf(dnB * kDn * interreflect);
          gndData[o + 3] = half(1);
        }
      }
    }

    // Re-upload into the existing 3D textures when the grid has not changed
    // shape — a bake happens on every sun change, and dispose/recreate is two
    // GPU allocations a shot for data that is exactly the same size.
    const sameDims = iv.skyTex && iv.dims.x === nx && iv.dims.y === ny && iv.dims.z === nz;
    const mk = (data) => {
      const t = new THREE.Data3DTexture(data, nx, ny, nz);
      t.format = THREE.RGBAFormat;
      t.type = THREE.HalfFloatType;
      t.minFilter = THREE.LinearFilter;
      t.magFilter = THREE.LinearFilter;
      t.wrapS = t.wrapT = t.wrapR = THREE.ClampToEdgeWrapping;
      t.colorSpace = THREE.NoColorSpace;
      t.needsUpdate = true;
      return t;
    };

    if (sameDims) {
      iv.skyTex.image.data = skyData;
      iv.groundTex.image.data = gndData;
      iv.skyTex.needsUpdate = true;
      iv.groundTex.needsUpdate = true;
    } else {
      iv.skyTex?.dispose();
      iv.groundTex?.dispose();
      iv.skyTex = mk(skyData);
      iv.groundTex = mk(gndData);
    }
    iv.skyData = skyData;
    iv.groundData = gndData;
    iv.dims.set(nx, ny, nz);
    iv.min.copy(bounds.min);
    iv.size.copy(size);
    iv.cells = cells;
    iv.baked = baked;
    iv.relocated = relocated;
    iv.stranded = stranded;
    iv.ms = performance.now() - t0;
    iv.enabled = true;

    sharedUniforms.aaaIvSky.value = iv.skyTex;
    sharedUniforms.aaaIvGround.value = iv.groundTex;
    sharedUniforms.aaaIvMin.value.copy(bounds.min);
    sharedUniforms.aaaIvInvSize.value.set(1 / Math.max(1e-3, size.x), 1 / Math.max(1e-3, size.y), 1 / Math.max(1e-3, size.z));
    sharedUniforms.aaaIvTexelInset.value.set(0.5 / nx, 0.5 / ny, 0.5 / nz);
    /**
     * Half a cell, in world units. The shader offsets its lookup along the
     * surface normal by this before sampling.
     *
     * Without it a floor at y = 0 reads a grid layer 1.5 m underground — solid
     * rock, black and zero-visibility — and a ceiling reads the layer above the
     * roof, which is open sky. That second one is what painted recognisable
     * cloud shapes across every interior ceiling in the build: the ceiling's
     * sky-visibility interpolated up toward 0.5, so half the outdoor specular
     * environment survived indoors.
     */
    sharedUniforms.aaaIvNormalBias.value.set(
      size.x / Math.max(1, nx - 1) * 0.55,
      size.y / Math.max(1, ny - 1) * 0.55,
      size.z / Math.max(1, nz - 1) * 0.55,
    );
    sharedUniforms.aaaIvIntensity.value = 1;
    // The volume now carries sky + bounce, so the unoccluded PMREM diffuse is
    // dialled almost all the way out; a sliver is left in for the directional
    // detail a hemisphere-split probe cannot represent.
    sharedUniforms.aaaIblDiffuse.value = 0.12;
  }

  /* ---------------- CPU-side irradiance lookup ---------------- */

  /**
   * Trilinearly sample the baked irradiance volume on the CPU.
   *
   * The shader samples the same two 3D textures per fragment; this is the same
   * lookup for whoever needs one number for a *point* rather than a surface.
   * The exposure anchor is the caller that matters (Lighting.js): it is the
   * only term in the build that knows the difference between standing in a
   * sunlit square and standing in a shuttered room, and it must come from the
   * lighting model rather than from the rendered frame — a frame-derived anchor
   * would make `tools/exposure-check.mjs` compare the frame against itself.
   *
   * Returns { up, down, vis } — hemispherical irradiance in scene units above
   * and below the horizon, and cosine-weighted sky visibility in 0..1.
   */
  const _irrOut = { up: 0, down: 0, vis: 1, valid: false };
  const halfOf = THREE.DataUtils.fromHalfFloat;

  function sampleIrradiance(point, out = _irrOut) {
    out.up = 0; out.down = 0; out.vis = 1; out.valid = false;
    if (!iv.enabled || !iv.skyData || !iv.groundData) return out;
    const nx = iv.dims.x | 0, ny = iv.dims.y | 0, nz = iv.dims.z | 0;
    const cl = (v, n) => Math.min(n - 1, Math.max(0, v));
    const fx = cl((point.x - iv.min.x) / Math.max(1e-3, iv.size.x) * (nx - 1), nx);
    const fy = cl((point.y - iv.min.y) / Math.max(1e-3, iv.size.y) * (ny - 1), ny);
    const fz = cl((point.z - iv.min.z) / Math.max(1e-3, iv.size.z) * (nz - 1), nz);
    const x0 = Math.floor(fx), y0 = Math.floor(fy), z0 = Math.floor(fz);
    const x1 = Math.min(nx - 1, x0 + 1), y1 = Math.min(ny - 1, y0 + 1), z1 = Math.min(nz - 1, z0 + 1);
    const tx = fx - x0, ty = fy - y0, tz = fz - z0;

    let up = 0, down = 0, vis = 0;
    for (let k = 0; k < 8; k++) {
      const x = (k & 1) ? x1 : x0, y = (k & 2) ? y1 : y0, z = (k & 4) ? z1 : z0;
      const w = ((k & 1) ? tx : 1 - tx) * ((k & 2) ? ty : 1 - ty) * ((k & 4) ? tz : 1 - tz);
      if (w <= 0) continue;
      const o = ((z * ny * nx) + (y * nx) + x) * 4;
      up += w * (0.2126 * halfOf(iv.skyData[o]) + 0.7152 * halfOf(iv.skyData[o + 1]) + 0.0722 * halfOf(iv.skyData[o + 2]));
      down += w * (0.2126 * halfOf(iv.groundData[o]) + 0.7152 * halfOf(iv.groundData[o + 1]) + 0.0722 * halfOf(iv.groundData[o + 2]));
      vis += w * halfOf(iv.skyData[o + 3]);
    }
    out.up = up; out.down = down; out.vis = vis; out.valid = true;
    return out;
  }

  /* ---------------- api ---------------- */

  const api = {
    probes,
    iv,
    sampleIrradiance,
    get environment() { return skyEnv; },

    init(context) {
      ctx = context;
      buildSkyEnvironment();
      return api;
    },

    /** Re-derive everything that depends on the sun. */
    refresh({ hideForBake, bakeProbes: doProbes = true, bakeVolume = true, budgetMs } = {}) {
      buildSkyEnvironment();
      if (doProbes) bakeProbes(hideForBake);
      if (bakeVolume) {
        const sunDir = ctx.sky?.sunDirection;
        const moved = !sunDir || lastBakeSun.dot(sunDir) < 0.9985;
        if (moved || !iv.enabled) {
          // Raised from 800/1600: the bake now also probes each cell for being
          // buried in geometry and relocates it, and reaches 42 m instead of
          // 26 m. Running out of budget no longer writes black, but it does
          // write a coarse analytic answer, and this volume is the frame's whole
          // diffuse ambient — it is worth the extra half second at a sun change.
          bakeIrradianceVolume({ budgetMs: budgetMs ?? (iv.enabled ? 2000 : 3000) });
          if (sunDir) lastBakeSun.copy(sunDir);
        }
      }
    },

    rebuildSkyEnvironment: buildSkyEnvironment,
    bakeProbes,
    bakeIrradianceVolume,
    assignProbes,

    setIrradianceIntensity(v) { sharedUniforms.aaaIvIntensity.value = v; },
    setSkyOcclusion(v) { sharedUniforms.aaaSkyOcclusion.value = v; },

    dispose() {
      disposeProbes();
      iv.skyTex?.dispose();
      iv.groundTex?.dispose();
      skyEnvRT?.dispose();       // the TARGET, not the texture — see buildSkyEnvironment
      skyEnvRT = null; skyEnv = null;
      pmremSky?.dispose();
      pmremProbe?.dispose();
      pmremSky = pmremProbe = null;
    },
  };

  return api;
}
